import { SdkContext, toLegacyError } from './internal'

export interface DownloadedFile {
  blob: Blob
  /** From `Content-Disposition`, or the fallback. */
  filename: string
}

const filenameOf = (disposition: string | null, fallback: string): string => {
  if (!disposition) return fallback
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition)
  if (star) {
    try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')) } catch { /* falls through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain ? plain[1].trim() : fallback
}

/**
 * A binary (or non-JSON) GET as a Blob. Not through `client.request`: the transport reads every
 * non-JSON body as text. Goes straight to fetch with the headers the client would send, and turns
 * a JSON refusal into the axios-like error the screens already read.
 */
/**
 * A big export (above 2,000 rows) is not in the response: the API answers 202 and e-mails a
 * link once the export queue has built the file.
 */
export interface QueuedExport {
  status: 'queued'
  exportId: string
  rows: number
  email: string
}

/** Reads a 202 body, whole envelope or its `data`. */
export const queuedExportOf = (result: unknown): QueuedExport | null => {
  if (!result || typeof result !== 'object') return null
  const r = result as { status?: unknown; data?: unknown }
  if (r.status === 'queued') return result as QueuedExport
  const data = r.data as { status?: unknown } | undefined
  return data && typeof data === 'object' && data.status === 'queued' ? (data as QueuedExport) : null
}

type Query = Record<string, string | number | boolean | null | undefined>

async function request(ctx: SdkContext, path: string, query: Query, accept: string, init: { method?: 'GET' | 'POST'; body?: unknown } = {}): Promise<Response> {
  const client = await ctx.ready
  const state = client.config
  const headers: Record<string, string> = { Accept: `${accept}, application/json` }
  if (state.token) headers.Authorization = 'Bearer ' + state.token
  if (state.communityId) headers['X-CommunityId'] = state.communityId
  if (state.memberId) headers['X-MemberId'] = state.memberId
  if (state.language) headers['Accept-Language'] = state.language
  const base = String(state.baseUrl || '').replace(/\/+$/, '')
  const search = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  })
  const qs = search.toString()
  const method = init.method || 'GET'
  if (method !== 'GET') headers['Content-Type'] = 'application/json'
  const response = await fetch(`${base}/api${path}${qs ? `?${qs}` : ''}`, {
    method,
    headers,
    ...(method !== 'GET' ? { body: JSON.stringify(init.body ?? {}) } : {})
  })
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}))
    throw toLegacyError({ name: 'MemberfyError', message: payload?.message || response.statusText, status: response.status, body: payload })
  }
  return response
}

export async function fetchFile(ctx: SdkContext, path: string, query: Query, accept: string, fallbackName: string): Promise<DownloadedFile> {
  const response = await request(ctx, path, query, accept)
  return { blob: await response.blob(), filename: filenameOf(response.headers.get('Content-Disposition'), fallbackName) }
}

/** A binary answer to a POST with a JSON body (a preview PDF built from what is on screen). */
export async function postFile(ctx: SdkContext, path: string, body: unknown, accept: string, fallbackName: string): Promise<DownloadedFile> {
  const response = await request(ctx, path, {}, accept, { method: 'POST', body })
  return { blob: await response.blob(), filename: filenameOf(response.headers.get('Content-Disposition'), fallbackName) }
}

/** Like `fetchFile`, for the export routes that queue big files (202): the file, or the queued export. */
export async function fetchExport(
  ctx: SdkContext,
  path: string,
  query: Query,
  accept: string,
  fallbackName: string
): Promise<DownloadedFile | QueuedExport> {
  const response = await request(ctx, path, query, accept)
  if (response.status === 202) {
    const queued = queuedExportOf(await response.json().catch(() => null))
    if (queued) return queued
  }
  return { blob: await response.blob(), filename: filenameOf(response.headers.get('Content-Disposition'), fallbackName) }
}

/** Saves a Blob through the browser. */
export const saveFile = ({ blob, filename }: DownloadedFile) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
